"use client"

import { useState, useEffect, useMemo } from "react"
import { X, Plus, Trash2 } from "lucide-react"

const labelOptions = ['Vacation', 'Holiday', 'Visit', 'Training', 'Personal', 'Custom']
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const parseHHMMToMin = (s) => {
  if (typeof s !== 'string') return null
  const m = s.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = +m[1], mi = +m[2]
  if (h < 0 || h > 23 || mi < 0 || mi > 59) return null
  return h * 60 + mi
}

// Accepts "9:00 AM", "9 AM", "09:00", "17:00" — covers the human-readable
// strings the default workingHours state stores for cleaners who never had
// availability normalized.
const parseFlexibleTimeToMin = (s) => {
  if (typeof s !== 'string') return null
  const trimmed = s.trim()
  const h24 = trimmed.match(/^(\d{1,2}):(\d{2})$/)
  if (h24) {
    const h = +h24[1], mi = +h24[2]
    if (h < 0 || h > 23 || mi < 0 || mi > 59) return null
    return h * 60 + mi
  }
  const ampm = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i)
  if (ampm) {
    let h = +ampm[1]
    const mi = ampm[2] ? +ampm[2] : 0
    const isPM = ampm[3].toUpperCase() === 'PM'
    if (h === 12) h = 0
    if (isPM) h += 12
    if (h < 0 || h > 23 || mi < 0 || mi > 59) return null
    return h * 60 + mi
  }
  return null
}

const parseHoursRange = (hoursStr) => {
  if (typeof hoursStr !== 'string') return null
  const parts = hoursStr.split(/\s*-\s*/)
  if (parts.length !== 2) return null
  const s = parseFlexibleTimeToMin(parts[0])
  const e = parseFlexibleTimeToMin(parts[1])
  if (s == null || e == null || e <= s) return null
  return [s, e]
}

const minToHHMM = (n) => {
  const h = Math.floor(n / 60)
  const mi = n % 60
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
}

const minToLabel = (n) => {
  const h = Math.floor(n / 60), mi = n % 60
  const period = h >= 12 ? 'PM' : 'AM'
  const hh = h === 0 ? 12 : h > 12 ? h - 12 : h
  return mi === 0 ? `${hh}${period.toLowerCase()}` : `${hh}:${String(mi).padStart(2, '0')}${period.toLowerCase()}`
}

// Resolve the cleaner's regular shift intervals for a specific date, in
// minutes-since-midnight pairs. Reads the `workingHours` state shape used
// by team-member-details.jsx: { [dayName]: { available, timeSlots: [{start,end,enabled}] } }.
const getRegularSlotsForDate = (workingHours, dateStr) => {
  if (!workingHours || !dateStr) return []
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return []
  const d = new Date(+m[1], +m[2] - 1, +m[3])
  if (Number.isNaN(d.getTime())) return []
  const dayData = workingHours[DAY_NAMES[d.getDay()]]
  if (!dayData || dayData.available === false) return []
  // 1) Normalized shape: timeSlots array of {start,end,enabled}.
  const slots = Array.isArray(dayData.timeSlots) ? dayData.timeSlots.filter(s => s && s.enabled !== false) : []
  if (slots.length > 0) {
    const ivs = slots
      .map(s => [parseHHMMToMin(s.start), parseHHMMToMin(s.end)])
      .filter(([s, e]) => s != null && e != null && e > s)
      .sort((a, b) => a[0] - b[0])
    if (ivs.length) return ivs
  }
  // 2) Legacy/default shape: human-readable "9:00 AM - 6:00 PM" string.
  if (typeof dayData.hours === 'string' && dayData.hours.trim()) {
    const iv = parseHoursRange(dayData.hours)
    if (iv) return [iv]
  }
  // 3) ZB canonical shape: direct start/end on the day entry.
  if (dayData.start && dayData.end) {
    const s = parseHHMMToMin(dayData.start)
    const e = parseHHMMToMin(dayData.end)
    if (s != null && e != null && e > s) return [[s, e]]
  }
  // 4) Marked available but no concrete hours stored — mirror the Weekly
  //    Hours UI, which falls back to "9:00 AM - 6:00 PM" when hours is
  //    empty (team-member-details.jsx uses the same default label). This
  //    is common for cleaners whose workingHours row was created with
  //    available=true but no explicit shift.
  if (dayData.available === true) return [[9 * 60, 18 * 60]]
  return []
}

// base minus [offStart, offEnd], for an array of base intervals.
const subtractOneInterval = (base, offStart, offEnd) => {
  const out = []
  for (const [bs, be] of base) {
    if (offEnd <= bs || offStart >= be) { out.push([bs, be]); continue }
    if (offStart > bs) out.push([bs, offStart])
    if (offEnd < be) out.push([offEnd, be])
  }
  return out
}

// Suggested off-window defaults given regular slots and chosen scope.
// Morning = regular start → min(noon, regular end). Afternoon = max(noon, regular start) → regular end.
const suggestOffWindow = (regular, scope) => {
  if (!regular.length) return null
  const regStart = regular[0][0]
  const regEnd = regular[regular.length - 1][1]
  const noon = 12 * 60
  if (scope === 'morning') {
    const end = Math.min(noon, regEnd)
    if (end <= regStart) return null
    return { start: minToHHMM(regStart), end: minToHHMM(end) }
  }
  if (scope === 'afternoon') {
    const start = Math.max(noon, regStart)
    if (start >= regEnd) return null
    return { start: minToHHMM(start), end: minToHHMM(regEnd) }
  }
  // custom: default to a middle slice
  const mid = Math.floor((regStart + regEnd) / 2)
  return { start: minToHHMM(regStart), end: minToHHMM(Math.min(mid + 60, regEnd)) }
}

const DateOverrideModal = ({ isOpen, onClose, onSave, existingOverride, defaultMode = 'unavailable', workingHours = null }) => {
  const [override, setOverride] = useState({
    date: '',
    available: false,
    hours: [],
    label: ''
  })
  // Time Off scope: 'all_day' | 'morning' | 'afternoon' | 'custom'
  const [scope, setScope] = useState('all_day')
  const [offWindow, setOffWindow] = useState({ start: '12:00', end: '18:00' })
  const [errors, setErrors] = useState({})

  const isEditMode = !!existingOverride
  const isCustomHoursMode = defaultMode === 'custom_hours'

  const regularSlots = useMemo(
    () => getRegularSlotsForDate(workingHours, override.date),
    [workingHours, override.date]
  )
  const hasRegularShift = regularSlots.length > 0

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      if (existingOverride) {
        // Legacy entries may have hours as a string ("09:00-17:00") — normalize to array.
        let hoursArr = existingOverride.hours
        if (typeof hoursArr === 'string') {
          if (hoursArr.toLowerCase() === 'unavailable' || !hoursArr.trim()) {
            hoursArr = []
          } else {
            hoursArr = hoursArr.split(',').map(seg => {
              const [s, e] = seg.trim().split('-')
              return { start: (s || '').trim(), end: (e || '').trim() }
            }).filter(h => h.start && h.end)
          }
        }
        if (!Array.isArray(hoursArr)) hoursArr = []
        setOverride({
          date: existingOverride.date || '',
          available: existingOverride.available !== false,
          hours: hoursArr,
          label: existingOverride.label || ''
        })
        // Infer scope from existing entry (partial off = reduced shift vs regular).
        if (!isCustomHoursMode && existingOverride.date) {
          if (existingOverride.available === false) {
            setScope('all_day')
          } else {
            const reg = getRegularSlotsForDate(workingHours, existingOverride.date)
            if (reg.length === 0) {
              setScope('all_day')
            } else {
              const availSum = hoursArr.reduce((n, h) => {
                const s = parseHHMMToMin(h.start), e = parseHHMMToMin(h.end)
                return s != null && e != null ? n + Math.max(0, e - s) : n
              }, 0)
              const regSum = reg.reduce((n, [s, e]) => n + (e - s), 0)
              setScope(availSum < regSum ? 'custom' : 'all_day')
            }
          }
        }
      } else {
        setOverride({
          date: '',
          available: isCustomHoursMode,
          hours: isCustomHoursMode ? [{ start: '09:00', end: '17:00' }] : [],
          label: ''
        })
        setScope('all_day')
      }
      setErrors({})
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => { document.body.style.overflow = 'unset' }
  }, [isOpen, existingOverride, isCustomHoursMode, workingHours])

  // When scope flips to morning/afternoon/custom, prefill the off-window from
  // the cleaner's regular shift for that date (if we have one).
  useEffect(() => {
    if (isCustomHoursMode) return
    if (scope === 'all_day') return
    if (!hasRegularShift) return
    const suggested = suggestOffWindow(regularSlots, scope)
    if (suggested) setOffWindow(suggested)
  }, [scope, hasRegularShift, regularSlots, isCustomHoursMode])


  // Remaining-available preview when a partial scope is selected — gives
  // the operator confidence they're writing the right override.
  const remainingPreview = useMemo(() => {
    if (isCustomHoursMode || scope === 'all_day' || !hasRegularShift) return null
    const offStart = parseHHMMToMin(offWindow.start)
    const offEnd = parseHHMMToMin(offWindow.end)
    if (offStart == null || offEnd == null || offEnd <= offStart) return null
    const remaining = subtractOneInterval(regularSlots, offStart, offEnd)
    if (!remaining.length) return 'None — effectively a full day off'
    return remaining.map(([s, e]) => `${minToLabel(s)}–${minToLabel(e)}`).join(', ')
  }, [scope, offWindow, regularSlots, hasRegularShift, isCustomHoursMode])

  const handleSave = () => {
    const newErrors = {}
    if (!override.date) newErrors.date = 'Date is required'
    if (isCustomHoursMode) {
      if (override.hours.length === 0) {
        newErrors.hours = 'Add at least one time slot'
      } else {
        override.hours.forEach((h, i) => {
          if (!h.start || !h.end) {
            newErrors[`hours_${i}`] = 'Start and end time are required'
          } else if (h.start >= h.end) {
            newErrors[`hours_${i}`] = 'End time must be after start time'
          }
        })
      }
    } else if (scope !== 'all_day') {
      const offStart = parseHHMMToMin(offWindow.start)
      const offEnd = parseHHMMToMin(offWindow.end)
      if (offStart == null || offEnd == null) {
        newErrors.offWindow = 'Enter a valid time range'
      } else if (offEnd <= offStart) {
        newErrors.offWindow = 'End time must be after start time'
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    if (isCustomHoursMode) {
      onSave({ ...override, available: true, hours: override.hours })
      return
    }

    if (scope === 'all_day') {
      onSave({ ...override, available: false, hours: [] })
      return
    }

    // Partial off: compute the remaining available window as (regular − off),
    // store as available:true with those hours. Downstream math (free windows,
    // scheduled hours, payroll) already handles this shape — no backend changes.
    const offStart = parseHHMMToMin(offWindow.start)
    const offEnd = parseHHMMToMin(offWindow.end)
    if (!hasRegularShift) {
      // Nothing to subtract from — fall back to full day off.
      onSave({ ...override, available: false, hours: [] })
      return
    }
    const remaining = subtractOneInterval(regularSlots, offStart, offEnd)
    if (remaining.length === 0) {
      onSave({ ...override, available: false, hours: [] })
      return
    }
    const hours = remaining.map(([s, e]) => ({ start: minToHHMM(s), end: minToHHMM(e) }))
    onSave({ ...override, available: true, hours })
  }

  const addTimeSlot = () => {
    setOverride(prev => ({
      ...prev,
      hours: [...prev.hours, { start: '09:00', end: '17:00' }]
    }))
  }

  const removeTimeSlot = (index) => {
    setOverride(prev => ({
      ...prev,
      hours: prev.hours.filter((_, i) => i !== index)
    }))
  }

  const updateTimeSlot = (index, field, value) => {
    setOverride(prev => ({
      ...prev,
      hours: prev.hours.map((h, i) => i === index ? { ...h, [field]: value } : h)
    }))
  }

  if (!isOpen) return null

  const modeTitle = isCustomHoursMode
    ? (isEditMode ? 'Edit Additional Hours' : 'Add Additional Hours')
    : (isEditMode ? 'Edit Time Off' : 'Add Time Off')
  const saveButtonLabel = isEditMode
    ? 'Save Changes'
    : (isCustomHoursMode ? 'Add Hours' : 'Add Time Off')

  const scopeOptions = [
    { id: 'all_day',   label: 'Whole day' },
    { id: 'morning',   label: 'Morning' },
    { id: 'afternoon', label: 'Afternoon' },
    { id: 'custom',    label: 'Custom' },
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-[var(--sf-border-light)]">
          <h2 className="text-lg font-semibold text-[var(--sf-text-primary)]">
            {modeTitle}
          </h2>
          <button onClick={onClose} className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-secondary)] p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">Date</label>
            <input
              type="date"
              value={override.date}
              onChange={e => setOverride(prev => ({ ...prev, date: e.target.value }))}
              className="w-full border border-[var(--sf-border-light)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]"
            />
            {errors.date && <p className="text-xs text-red-600 mt-1">{errors.date}</p>}
          </div>

          {/* Scope picker — only for Time Off mode */}
          {!isCustomHoursMode && (
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">Scope</label>
              <div className="flex flex-wrap gap-2">
                {scopeOptions.map(opt => {
                  const disabled = opt.id !== 'all_day' && override.date && !hasRegularShift
                  const active = scope === opt.id
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setScope(opt.id)}
                      title={disabled ? 'No regular hours on this day — only "Whole day" applies' : ''}
                      className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                        active
                          ? 'bg-[var(--sf-blue-500)] text-white border-[var(--sf-blue-500)]'
                          : disabled
                            ? 'bg-[var(--sf-bg-page)] text-[var(--sf-text-muted)] border-[var(--sf-border-light)] cursor-not-allowed'
                            : 'bg-white text-[var(--sf-text-primary)] border-[var(--sf-border-light)] hover:border-[var(--sf-blue-500)] hover:text-[var(--sf-blue-500)]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>

              {scope !== 'all_day' && (
                <div className="mt-3 p-3 border border-[var(--sf-border-light)] rounded-lg bg-[var(--sf-bg-page)]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[var(--sf-text-secondary)]">Unavailable from</span>
                    <input
                      type="time"
                      value={offWindow.start}
                      onChange={e => setOffWindow(prev => ({ ...prev, start: e.target.value }))}
                      className="border border-[var(--sf-border-light)] rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]"
                    />
                    <span className="text-xs text-[var(--sf-text-muted)]">to</span>
                    <input
                      type="time"
                      value={offWindow.end}
                      onChange={e => setOffWindow(prev => ({ ...prev, end: e.target.value }))}
                      className="border border-[var(--sf-border-light)] rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]"
                    />
                  </div>
                  {errors.offWindow && <p className="text-xs text-red-600 mt-2">{errors.offWindow}</p>}
                  {remainingPreview && (
                    <p className="text-xs text-[var(--sf-text-muted)] mt-2">
                      Still available: <span className="font-medium text-[var(--sf-text-secondary)]">{remainingPreview}</span>
                    </p>
                  )}
                </div>
              )}

              {override.date && !hasRegularShift && scope === 'all_day' && (
                <p className="text-xs text-[var(--sf-text-muted)] mt-2">
                  No regular shift on this day — only "Whole day" applies.
                </p>
              )}
            </div>
          )}

          {/* Label / Reason */}
          <div>
            <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">Reason</label>
            <select
              value={override.label}
              onChange={e => setOverride(prev => ({ ...prev, label: e.target.value }))}
              className="w-full border border-[var(--sf-border-light)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)] bg-white"
            >
              <option value="">Select reason (optional)</option>
              {labelOptions.map(label => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </div>

          {/* Custom time slots (only when adding Additional Hours) */}
          {isCustomHoursMode && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[var(--sf-text-primary)]">Time Slots</label>
                <button
                  type="button"
                  onClick={addTimeSlot}
                  className="text-[var(--sf-blue-500)] hover:text-[var(--sf-blue-500)] text-sm font-medium flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Slot
                </button>
              </div>
              {override.hours.length === 0 && (
                <p className="text-xs text-[var(--sf-text-muted)]">No custom time slots. Regular working hours will apply.</p>
              )}
              <div className="space-y-2">
                {override.hours.map((slot, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="time"
                      value={slot.start}
                      onChange={e => updateTimeSlot(index, 'start', e.target.value)}
                      className="flex-1 border border-[var(--sf-border-light)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]"
                    />
                    <span className="text-[var(--sf-text-muted)] text-sm">to</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={e => updateTimeSlot(index, 'end', e.target.value)}
                      className="flex-1 border border-[var(--sf-border-light)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]"
                    />
                    <button
                      type="button"
                      onClick={() => removeTimeSlot(index)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {errors[`hours_${index}`] && (
                      <p className="text-xs text-red-600">{errors[`hours_${index}`]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-[var(--sf-border-light)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--sf-text-primary)] bg-white border border-[var(--sf-border-light)] rounded-lg hover:bg-[var(--sf-bg-page)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-[var(--sf-blue-500)] rounded-lg hover:bg-[var(--sf-blue-600)]"
          >
            {saveButtonLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DateOverrideModal
